// src/theme/components.ts
import { COLORS } from './colors';

interface LanguageSelectorStyles {
  dropdown: {
    base: string;
    item: string;
    hover: string;
    active: string;
  }
}

export const COMPONENT_STYLES = {
  // Кнопки (оставляем как есть)
  button: {
    base: 'rounded-lg transition-colors duration-200 font-medium',
    primary: `bg-primary hover:bg-primary/90 text-white`,
    secondary: `bg-[${COLORS.secondary}] hover:bg-[${COLORS.secondary}]/90 text-white`,
    text: `text-primary hover:text-primary/80`,
    outline: `border border-primary text-primary hover:bg-primary hover:text-white`,
    toolbarButton: `flex items-center px-4 py-2 
      border border-[${COLORS.secondary}] 
      rounded-lg 
      text-[${COLORS.text}]
      hover:bg-[${COLORS.background}] 
      transition-colors duration-200`,
    active: `bg-primary/10 text-primary`,
    sizes: {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2',
      lg: 'px-6 py-3 text-lg'
    }
  },
  
  // Карточки (оставляем как есть)
  card: {
    base: 'bg-white rounded-lg shadow-sm overflow-hidden',
    toolbar: `bg-[${COLORS.background}] rounded-lg shadow-sm p-4 w-full overflow-hidden`,
    header: 'px-6 py-4 border-b border-[#A7ABAA]/20',
    body: 'p-6'
  },
  
  // Поля ввода (оставляем как есть)
  input: {
    base: `w-full rounded-lg border border-[${COLORS.secondary}] 
      focus:ring-2 focus:ring-primary 
      focus:border-transparent transition duration-200`,
    search: `w-full pl-10 pr-4 py-2 
      border border-[${COLORS.secondary}] 
      rounded-lg 
      text-[${COLORS.text}]
      placeholder:text-[${COLORS.secondary}]
      focus:ring-2 
      focus:ring-primary 
      focus:border-transparent 
      transition-all duration-200`,
    sizes: {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2',
      lg: 'px-4 py-3 text-lg'
    }
  },
  
  // Иконки (оставляем как есть)
  icon: {
    search: `w-5 h-5 text-[${COLORS.secondary}] absolute left-3 top-2.5 pointer-events-none`,
    button: 'w-5 h-5 mr-2',
    action: `w-5 h-5`
  },
  
  // Бейджи статусов (оставляем как есть)
  badge: {
    base: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
    cold: 'bg-[#A7ABAA]/20 text-[#465357]',
    new: 'bg-[#245D33]/20 text-[#245D33]',
    rare: 'bg-[#F59E0B]/20 text-[#B45309]',
    regular: 'bg-[#245D33]/40 text-[#245D33]',
    loyal: 'bg-[#245D33] text-white',
    lost: 'bg-[#212122]/20 text-[#212122]'
  },

  // Языковой переключатель (оставляем как есть)
  languageSelector: {
    dropdown: {
      base: 'absolute right-0 bottom-full mb-2 w-32 bg-white rounded-lg shadow-lg',
      item: 'w-full text-left px-4 py-2',
      hover: 'hover:bg-[#F5F5F5]',
      active: 'bg-[#F5F5F5]'
    }
  } as LanguageSelectorStyles,
  
  // Добавляем стили для текста диаграмм
  chartTextColors: {
    // Для статусов клиентов
    new: 'text-[#4A90E2]',
    rare: 'text-[#50E3C2]',
    regular: 'text-[#F5A623]',
    loyal: 'text-[#7ED321]',
    lost: 'text-[#D0021B]',
    cold: 'text-[#607D8B]',
    // Для источников привлечения
    organic: 'text-[#4CAF50]',
    direct: 'text-[#2196F3]',
    referral: 'text-[#FF9800]',
    social: 'text-[#E91E63]',
    whatsapp: 'text-[#25D366]',
    instagram: 'text-[#E1306C]',
    telegram: 'text-[#0088CC]',
    website: 'text-[#4285F4]',
    // Значение по умолчанию
    default: 'text-[#245D33]'
  },
  
  // Добавляем стили для сообщений (оставляем как есть)
  message: {
    base: 'p-3 rounded-lg text-sm mb-4',
    error: `${COLORS.status.error.bg} ${COLORS.status.error.text} p-3 rounded-lg text-sm mb-4`,
    warning: `${COLORS.status.warning.bg} ${COLORS.status.warning.text} p-3 rounded-lg text-sm mb-4`,
    info: `${COLORS.status.info.bg} ${COLORS.status.info.text} p-3 rounded-lg text-sm mb-4`,
    success: `${COLORS.status.success.bg} ${COLORS.status.success.text} p-3 rounded-lg text-sm mb-4`,
    debug: `bg-yellow-100 text-yellow-800 p-3 rounded-lg text-sm mb-4 overflow-auto max-h-60`,
  },
  
  // Добавляем стили для форм (оставляем как есть)
  form: {
    container: 'space-y-6',
    group: 'space-y-1 mb-4',
    label: 'block text-sm font-medium text-[#465357]',
    input: 'w-full rounded-lg border border-[#A7ABAA] px-4 py-2 focus:ring-2 focus:ring-[#245D33] focus:border-transparent',
    checkbox: {
      container: 'flex items-center',
      input: 'h-4 w-4 rounded border-gray-300 text-[#245D33] focus:ring-[#245D33]',
      label: 'ml-2 block text-sm text-[#465357]'
    },
    divider: {
      container: 'relative my-6',
      line: 'absolute inset-0 flex items-center',
      lineInner: 'w-full border-t border-gray-300',
      textContainer: 'relative flex justify-center text-sm',
      text: 'px-2 bg-white text-[#A7ABAA]'
    }
  },
  
  // Добавляем стили для аутентификации (оставляем как есть)
  auth: {
    modal: {
      container: 'fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4',
      overlay: 'fixed inset-0 bg-black opacity-40',
      content: 'bg-white rounded-lg shadow-xl w-full max-w-md z-10 relative my-4 max-h-[90vh] flex flex-col',
      header: 'flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg',
      body: 'p-6 overflow-y-auto',
      closeButton: 'text-gray-500 hover:text-gray-700 transition-colors duration-200'
    },
    socialButtons: {
      container: 'grid grid-cols-2 gap-3',
      button: 'w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50',
      facebook: 'text-blue-600',
      google: 'text-red-500'
    }
  },
  
  // Toolbar специфические стили (оставляем как есть)
  toolbar: {
    container: `bg-[${COLORS.background}] rounded-lg shadow-sm p-4 w-full overflow-hidden`,
    searchContainer: 'relative flex-1 max-w-md',
    buttonContainer: 'flex gap-2 w-full md:w-auto',
    actionButton: `flex items-center gap-2 mt-2 md:mt-0`
  }
} as const;